import { Component, signal, computed, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UserService } from '../../services/user.service';
import { SmsService } from '../../services/sms.service';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Router, RouterLink } from "@angular/router";
import { LogoComponent } from "src/app/components/logo/logo.component";
import { UserProfileAvatarComponent } from "../../components/user-profile-avatar/user-profile-avatar.component";
import { TokenService } from '../../services/token.service';

interface ApiApplication {
  id: string;
  isDeleted: boolean;
  createdDate: string;
  status: string;
  zipcode?: string;
  householdSize?: string;
  submittedDate?: string;
  coveragePeriod?: number;
  applicationData?: {
    coveragePeriod?: number;
  };
}

interface ApiUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  createdDate: string;
  registrationStatus?: string;
  applications?: ApiApplication[];
}

/**
 * IntakeDashboardComponent
 * * The main dashboard view for the application.
 * Responsibilities:
 * - Fetches and displays a list of users/recipients.
 * - calculates statistics (completed, in-progress, etc.).
 * - Provides search and filtering functionality.
 * - Handles sending SMS invitations.
 */
@Component({
  selector: 'app-intake-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, NzSkeletonModule, RouterLink, LogoComponent, UserProfileAvatarComponent],
  templateUrl: './intake-dashboard.component.html',
  styleUrl: './intake-dashboard.component.scss',
})
export class IntakeDashboardComponent implements OnInit {
  /** Service to manage user data (fetching list, etc.) */
  private userService = inject(UserService);
  /** Service to handle SMS communications */
  private smsService = inject(SmsService);
  /** Service for displaying toast notifications */
  private notification = inject(NzNotificationService);
  /** Router for navigation actions */
  private router = inject(Router);
  /** Token service for auth management (logout) */
  private tokenService = inject(TokenService);

  // --- Loading State ---
  /** Signal to track if data is currently being fetched */
  isLoading = signal(true);

  // --- Data State ---
  /** Signal for the current search query typed by the user */
  searchText = signal('');
  /** Signal for the currently active status filter (All, New, etc.) */
  activeFilter = signal('All');
  /** Signal holding the raw list of mapped recipients */
  recipients = signal<any[]>([]);

  // --- Pagination State ---
  /** Current page number for the table pagination */
  currentPage = signal(1);
  /** Number of items to show per page */
  pageSize = 20;

  // --- Stats State ---
  /** * Signal holding dashboard statistics.
   * trends and changes are currently placeholders or calculated based on basic logic.
   */
  stats = signal({
    total: { value: 0, change: '+0%', trend: 'up' },
    completed: { value: 0, change: '+0%', trend: 'up' },
    inProgress: { value: 0, change: '+0%', trend: 'up' },
    newSignups: { value: 0, change: '0%', trend: 'down' }
  });

  /**
   * Angular Lifecycle Hook: Request user data on component initialization.
   */
  ngOnInit() {
    this.fetchUserData();
  }

  /**
   * Fetches all users from the backend service.
   * Maps the raw API response to the local Recipient model and updates the stats.
   */
  fetchUserData() {
    // Set loading state to true before API call
    this.isLoading.set(true);
    
    this.userService.getUsers().subscribe({
      next: (users: ApiUser[]) => {
        // Transform API data to UI model
        const mappedUsers = users.map(user => this.mapApiUserToRecipient(user));
        
        // Update signals
        this.recipients.set(mappedUsers);
        this.updateStats(mappedUsers);
        
        // Disable loading state
        this.isLoading.set(false);
      },
      error: (err) => {
        // Keep existing error log
        console.error('Failed to load user data', err);
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Logs out the current user.
   * Clears the authentication token and redirects to the login page.
   */
  logout() {
    this.tokenService.clearToken();
    this.router.navigate(['/login']);
  }

  /**
   * Transforms a raw API User object into a formatted Recipient object for the UI.
   * Handles logic for determining application status, icons, and colors.
   * * @param user - The raw user object from the API.
   * @returns A formatted object ready for the template.
   */
  private mapApiUserToRecipient(user: ApiUser) {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim() || 'Unknown User';
    const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase() || '??';
    const formattedPhone = this.formatPhoneNumber(user.phoneNumber);

    // Determine the most relevant application (usually the latest active one)
    const latestApp = this.getLatestApplication(user.applications);

    let appStatus = 'No Application';
    let coveragePeriod = 30;
    let submissionDateFormatted = '-';

    // If an application exists, extract status and dates
    if (latestApp) {
      appStatus = latestApp.status ? latestApp.status : 'Draft';
      
      // Handle coverage period location (it might vary in structure)
      if (latestApp.coveragePeriod !== undefined && latestApp.coveragePeriod !== null) {
        coveragePeriod = latestApp.coveragePeriod;
      } else if (latestApp.applicationData?.coveragePeriod !== undefined && latestApp.applicationData?.coveragePeriod !== null) {
        coveragePeriod = latestApp.applicationData.coveragePeriod;
      }

      // Format submission date
      const rawSubmissionDate = latestApp.submittedDate || latestApp.createdDate;
      if (rawSubmissionDate) {
        submissionDateFormatted = new Date(rawSubmissionDate).toLocaleDateString('en-US', {
          month: 'short', day: '2-digit', year: 'numeric'
        });
      }
    }

    // Get UI config (icon, color) based on the calculated status
    const statusConfig = this.getStatusConfig(appStatus);
    const dateAdded = new Date(user.createdDate).toLocaleDateString('en-US', {
      month: 'short', day: '2-digit', year: 'numeric'
    });

    // Determine registration status (e.g., if they have logged in/applied)
    let registrationStatus = user.registrationStatus || 'Invited';
    if (latestApp) {
      registrationStatus = 'Active';
    }

    return {
      id: user.id,
      initials: initials,
      color: 'bg-indigo-100 text-indigo-600',
      name: fullName,
      firstName: firstName.toLowerCase(),
      lastName: lastName.toLowerCase(),
      email: user.email,
      phone: formattedPhone,
      liifeIdStatus: appStatus,
      liifeIdIcon: statusConfig.icon,
      liifeIdColor: statusConfig.colorClass,
      zipcode: latestApp?.zipcode || '-',
      householdSize: latestApp?.householdSize || '-',
      status: registrationStatus,
      statusColor: this.getRegistrationStatusColor(registrationStatus),
      date: dateAdded,
      submissionDate: submissionDateFormatted,
      coveragePeriod: coveragePeriod,
      application: latestApp ? { ...latestApp } : null
    };
  }

  /**
   * Helper to find the most recent non-deleted application from a list.
   * * @param applications - Array of applications from the API.
   * @returns The latest application or null.
   */
  private getLatestApplication(applications?: ApiApplication[]): ApiApplication | null {
    if (!applications || applications.length === 0) return null;
    
    // Filter out deleted apps
    const validApps = applications.filter(app => !app.isDeleted);
    if (validApps.length === 0) return null;
    
    // Sort by creation date descending
    validApps.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
    return validApps[0];
  }

  /**
   * Formats a raw E.164 phone number into a readable US format.
   * * @param phone - Phone string (e.g., +15551234567)
   * @returns Formatted string (e.g., +1 555-123-4567)
   */
  public formatPhoneNumber(phone: string): string {
    if (!phone) return '-';
    const regex = /^(\+1)(\d{3})(\d{3})(\d{4})$/;
    const match = phone.match(regex);
    if (match) {
      return `${match[1]} ${match[2]}-${match[3]}-${match[4]}`;
    }
    return phone;
  }

  /**
   * Returns UI configuration (Material Icon name and Tailwind color class) 
   * based on the application status string.
   */
  private getStatusConfig(status: string) {
    switch (status) {
      case 'Draft':
      case 'Submitted - Awaiting Response':
      case 'Pre-Submission Review':
      case 'Awaiting Applicant Updates':
        return { icon: 'schedule', colorClass: 'text-orange-500' };
      case 'Submitted to State':
        return { icon: 'send', colorClass: 'text-blue-500' };
      case 'Denied by State':
        return { icon: 'cancel', colorClass: 'text-red-500' };
      case 'Application Approved':
        return { icon: 'check_circle', colorClass: 'text-green-500' };
      default:
        return { icon: 'help_outline', colorClass: 'text-gray-400' };
    }
  }

  /**
   * Returns Tailwind CSS classes for the Registration Status badge.
   */
  private getRegistrationStatusColor(status: string) {
    switch (status.toLowerCase()) {
      case 'active': return 'bg-[#DCFCE7] text-[#166534]';
      case 'sms not sent': return 'bg-[#FEE2E2] text-[#991B1B]';
      default: return 'bg-[#FFF5E1] text-[#B95000]';
    }
  }

  /**
   * Computed Signal: Filters the recipient list based on search text and active category filter.
   * Also sorts the list alphabetically by first name.
   */
  filteredRecipients = computed(() => {
    const allUsers = [...this.recipients()];
    const search = this.searchText().toLowerCase();
    const filter = this.activeFilter();

    const filtered = allUsers.filter(user => {
      // 1. Search Logic
      const matchesSearch =
        (user.name?.toLowerCase().includes(search)) ||
        (user.email?.toLowerCase().includes(search)) ||
        (user.phone?.includes(search));

      // 2. Filter Logic
      let matchesFilter = true;
      if (filter === 'New') {
        matchesFilter = user.liifeIdStatus === 'No Application';
      } else if (filter === 'In Progress') {
        matchesFilter = ['Draft', 'Submitted - Awaiting Response', 'Submitted to State', 'Pre-Submission Review', 'Awaiting Applicant Updates'].includes(user.liifeIdStatus);
      } else if (filter === 'Submitted') {
        matchesFilter = ['Submitted - Awaiting Response', 'Submitted to State', 'Application Approved', 'Denied by State'].includes(user.liifeIdStatus);
      } else if (filter === 'Completed') {
        matchesFilter = user.liifeIdStatus === 'Application Approved';
      }

      return matchesSearch && matchesFilter;
    });

    // 3. Sort Logic
    return filtered.sort((a, b) => {
      if (a.firstName < b.firstName) return -1;
      if (a.firstName > b.firstName) return 1;
      if (a.lastName < b.lastName) return -1;
      if (a.lastName > b.lastName) return 1;
      return 0;
    });
  });

  /**
   * Computed Signal: Slices the filtered list for the current page.
   */
  paginatedRecipients = computed(() => {
    const users = this.filteredRecipients();
    const startIndex = (this.currentPage() - 1) * this.pageSize;
    return users.slice(startIndex, startIndex + this.pageSize);
  });

  /**
   * Computed Signal: Calculates total pages based on filtered count and page size.
   */
  totalPages = computed(() => Math.ceil(this.filteredRecipients().length / this.pageSize));

  /**
   * Sends an SMS registration link to the user.
   * * @param userId - The ID of the recipient.
   */
  sendInvite(userId: string) {
    if (!userId) return;

    this.smsService.sendRegistrationLink(userId).subscribe({
      next: (success) => {
        if (success) {
          this.notification.success('Invitation Sent', "The SMS invitation message successfully delivered to user's phone.");
        }
      },
      error: (err) => {
        // Keep existing error log
        console.error('Failed to send SMS', err);
        this.notification.error('Error', 'Failed to send the invitation SMS. Please try again.');
      }
    });
  }

  /**
   * Updates the search query signal when the user types in the input box.
   * Resets pagination to page 1.
   * * @param e - Input event.
   */
  updateSearch(e: Event) {
    const target = e.target as HTMLInputElement;
    this.searchText.set(target.value);
    this.currentPage.set(1);
  }

  /**
   * Updates the active filter category (All, New, etc.).
   * Resets pagination to page 1.
   * * @param filterName - The name of the filter to apply.
   */
  setFilter(filterName: string) {
    this.activeFilter.set(filterName);
    this.currentPage.set(1);
  }

  /**
   * Changes the current page number.
   * Includes boundary checks.
   * * @param page - Target page number.
   */
  changePage(page: number) {
    const total = this.totalPages();
    if (page >= 1 && page <= total) {
      this.currentPage.set(page);
    }
  }

  /**
   * Calculates dashboard statistics based on the processed recipient list.
   * Updates the 'stats' signal.
   * * @param users - The full list of processed users.
   */
  private updateStats(users: any[]) {
    const total = users.length;
    const inProgressStatuses = ['Draft', 'Submitted - Awaiting Response', 'Pre-Submission Review', 'Awaiting Applicant Updates', 'Submitted to State'];
    const completedStatuses = ['Application Approved', 'Denied by State'];

    let inProgressCount = 0;
    let completedCount = 0;

    users.forEach(user => {
      const status = user.liifeIdStatus;
      if (inProgressStatuses.includes(status)) inProgressCount++;
      else if (completedStatuses.includes(status)) completedCount++;
    });

    const newSignupsCount = total - completedCount - inProgressCount;

    this.stats.set({
      total: { value: total, change: '', trend: 'neutral' },
      completed: { value: completedCount, change: '', trend: 'neutral' },
      inProgress: { value: inProgressCount, change: '', trend: 'neutral' },
      newSignups: { value: newSignupsCount, change: '', trend: 'neutral' }
    });
  }
}